/**
 * The workspace switcher (spec M0-F4 §7.5).
 *
 * A 264-wide dropdown anchored under the 195×40 switcher button, in the observed order
 * (`[ui:library/shell-04-workspace-switcher.png]`): search field → **Recent workspaces** →
 * **My workspaces** → footer with **Browse all** and **+ Add workspace**.
 */
import { AnchoredPopover, Icon, Search, WorkspaceAvatar, icons } from '@elizmonday/ui';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useWorkspaceList } from '../../modules/shell/bootstrap';
import { usePreference } from '../../modules/shell/preferences';
import { boardUrl, workspaceUrl } from '../../modules/shell/urls';
import { NameDialog } from '../../modules/boards/NameDialog';
import { useWorkspaceMutations } from '../../modules/boards/workspaces';

export interface WorkspaceSummary {
  id: string;
  name: string;
  kind: string;
}

export interface WorkspaceSwitcherProps {
  current: WorkspaceSummary | null;
  canAddWorkspace: boolean;
}

/** Under 50 workspaces the filter is client-side; above it the query round-trips (W2). */
const CLIENT_FILTER_LIMIT = 50;

export function WorkspaceSwitcher({ current, canAddWorkspace }: WorkspaceSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  /**
   * P-005: the anchor for the **body-portalled** list. Vibe's `Dialog` rendered the list
   * inline inside the pane, so on a phone — where the pane is a drawer with its own stacking
   * context — the list painted *behind* the drawer and could not be tapped.
   */
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const mutations = useWorkspaceMutations();
  const [, setLastWorkspaceId] = usePreference('shell.lastWorkspaceId');
  const [lastBoardByWorkspace] = usePreference('shell.lastBoardIdByWorkspace');

  // The unfiltered list is always fetched; its `total` decides whether the filter needs the
  // server at all. Below the limit both hooks share a query key, so there is no second request.
  const base = useWorkspaceList('');
  const serverSide = (base.data?.total ?? 0) > CLIENT_FILTER_LIMIT;
  const remote = useWorkspaceList(serverSide ? query : '');
  const source = serverSide ? remote : base;

  const needle = query.trim().toLowerCase();
  const filter = (rows: WorkspaceSummary[]) =>
    serverSide || needle.length === 0 ? rows : rows.filter((row) => row.name.toLowerCase().includes(needle));

  const recent = useMemo(() => filter(source.data?.recent ?? []), [source.data, needle, serverSide]);
  const all = useMemo(() => filter(source.data?.all ?? []), [source.data, needle, serverSide]);

  /**
   * W3: choosing a workspace writes `shell.lastWorkspaceId` and lands on the last board of
   * that workspace when it still exists, otherwise on the workspace home.
   */
  function choose(workspace: WorkspaceSummary) {
    setLastWorkspaceId(workspace.id);
    setOpen(false);
    setQuery('');
    const lastBoard = lastBoardByWorkspace[workspace.id];
    navigate(lastBoard ? boardUrl(lastBoard) : workspaceUrl(workspace.id));
  }

  const menu = (
    <div className="ez-switcher-menu" data-testid="workspace-switcher-menu">
      <div className="ez-switcher-menu__search">
        <Search
          placeholder="Search for a workspace"
          inputAriaLabel="Search for a workspace"
          value={query}
          onChange={setQuery}
          debounceMs={200}
          data-testid="workspace-search"
        />
      </div>

      <div className="ez-switcher-menu__scroll">
        <div className="ez-switcher-menu__section">Recent workspaces</div>
        {recent.map((workspace) => (
          <button
            key={`recent-${workspace.id}`}
            type="button"
            className="ez-switcher-menu__row"
            /* P-163: a workspace row is addressable by its id, not only by its text. */
            data-testid={`workspace-row-recent-${workspace.id}`}
            aria-current={workspace.id === current?.id}
            onClick={() => choose(workspace)}
          >
            <WorkspaceAvatar name={workspace.name} size={24} />
            {workspace.name}
          </button>
        ))}

        <div className="ez-switcher-menu__section">My workspaces</div>
        {all.map((workspace) => (
          <button
            key={`all-${workspace.id}`}
            type="button"
            className="ez-switcher-menu__row"
            data-testid={`workspace-row-${workspace.id}`}
            aria-current={workspace.id === current?.id}
            onClick={() => choose(workspace)}
          >
            <WorkspaceAvatar name={workspace.name} size={24} />
            {workspace.name}
          </button>
        ))}
      </div>

      <div className="ez-switcher-menu__footer">
        <button
          type="button"
          className="ez-switcher-menu__row"
          onClick={() => {
            setOpen(false);
            navigate('/workspaces');
          }}
          data-testid="workspace-browse-all"
        >
          <Icon icon={icons.workspace} size={16} ignoreFocusStyle />
          Browse all
        </button>
        {canAddWorkspace ? (
          /**
           * B10: this was disabled with "arrives with M1.B1" long after B1 shipped the
           * dialog. monday's footer is `Browse all` + `Add workspace` `[c:sidebar-workspace-dropdown]`
           * and both work.
           */
          <button
            type="button"
            className="ez-switcher-menu__row"
            data-testid="workspace-add"
            onClick={() => {
              setOpen(false);
              setCreating(true);
            }}
          >
            <Icon icon={icons.add} size={16} ignoreFocusStyle />
            Add workspace
          </button>
        ) : null}
      </div>
    </div>
  );

  if (creating) {
    return (
      <NameDialog
        title="Add workspace"
        label="Workspace name"
        testId="create-workspace-dialog"
        onCancel={() => setCreating(false)}
        onSubmit={(name) => {
          void mutations.create.mutateAsync({ name }).then((created) => {
            setCreating(false);
            const id = created && typeof created === 'object' && 'id' in created
              ? String((created as { id: string | number }).id)
              : null;
            if (id) navigate(workspaceUrl(id));
          });
        }}
      />
    );
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="ez-switcher"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        data-testid="workspace-switcher"
      >
        <WorkspaceAvatar name={current?.name ?? '?'} size={24} />
        <span className="ez-switcher__name">{current?.name ?? 'No workspace'}</span>
        <Icon icon={icons.chevronDown} size={16} ignoreFocusStyle />
      </button>

      <AnchoredPopover
        anchor={triggerRef.current}
        open={open}
        onClose={() => setOpen(false)}
        label="Workspaces"
        role="listbox"
        /** Measured on monday: the dropdown is 280 px `[c:sidebar-workspace-dropdown]`. */
        width={280}
        data-testid="workspace-switcher-popover"
      >
        {menu}
      </AnchoredPopover>
    </>
  );
}
